import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  Box
} from "@mui/material";
import { Add, Cancel, Delete } from "@mui/icons-material";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { fetchTermDepositsInterest, fetchTermDepositsInterestPreMatureByParentId, fetchTermDeposits, createTermDeposits, createDepositInterest, createTermDepositPreMatureInterest, clearInterestData } from "../../redux/masters/termDepositSlice";
import EnhancedTable from "../common/EnhancedTable";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";


const InterestModal = ({ open, mode, onClose, rowData }) => {
  const showDate = rowData?.showDate || false;

  const dispatch = useDispatch();

  const {
    interestItems: interestItemsFromRedux,
    loading: interestLoading,   // 🔄 renamed from state.loading
    error: interestError,       // 🔄 renamed from state.error
    preMatureItems: preMatureItemsFromRedux,
    preMatureLoading,
    preMatureError,
  } = useSelector((state) => state.termDeposits);

  const { items: termDeposits } = useSelector((state) => state.termDeposits);
  const [duration, setDuration] = useState("");
  const [interest, setInterest] = useState("");

  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedInterest, setSelectedInterest] = useState("");
  const [selectedSlabId, setSelectedSlabId] = useState("");

  const [validFrom, setValidFrom] = useState(new Date()); // default today
  const [selectedDepositId, setSelectedDepositId] = useState("");
  const [interestErrorValue, setInterestErrorValue] = useState("");
  const [durationErrorValue, setDurationErrorValue] = useState("");

  const [adding, setAdding] = useState(false);
  // check which icon opened the modal
  const [isForkMode, setIsForkMode] = useState(false);

  const [loanNo, setLoanNo] = useState("");


  const minDuration = rowData?.minDepositDuration || 0;
  const maxDuration = rowData?.maxDepositDuration || 0;

  const [interestItems, setInterestItems] = useState([]);
  const [preMatureItems, setPreMatureItems] = useState([]);

  // Local state mirrors
  const [localInterestItems, setLocalInterestItems] = useState([]);
  const [localPreMatureItems, setLocalPreMatureItems] = useState([]);


  useEffect(() => {
  setInterestItems(interestItemsFromRedux || []);
}, [interestItemsFromRedux]);

useEffect(() => {
  setPreMatureItems(preMatureItemsFromRedux || []);
}, [preMatureItemsFromRedux]);

  useEffect(() => {
    if (open) {
      dispatch(clearInterestData());
    }
    if (open && rowData?.id) {
      if (isForkMode) {
        // ✅ Fork Mode → call Pre-Mature API
        dispatch(fetchTermDepositsInterestPreMatureByParentId(rowData?.id));
      } else {
        // ✅ Normal Mode → call regular Interest API
        dispatch(fetchTermDepositsInterest(rowData?.id));
      }
    }
  }, [open, dispatch, isForkMode]);

  useEffect(() => {
    if (mode === "fork") {
      setIsForkMode(true);
    } else {
      setIsForkMode(false);
    }


  }, [mode]);

  useEffect(() => {
    if (isForkMode && interestItems.length > 0) {
      const maxDuration = Math.max(...interestItems.map((item) => item.termDuration));
      const match = interestItems.find((item) => item.termDuration === maxDuration);

      if (match) {
        setSelectedDuration(match.termDuration);
        setSelectedInterest(match.interestRate);
        setSelectedSlabId(match.id);
      }
    }
  }, [interestItems, isForkMode]);


  // ✅ Find the max duration & interest from the dropdown list
  const maxDropdownDuration = interestItems.length
    ? Math.max(...interestItems.map((item) => item.termDuration))
    : 0;

  const maxDropdownInterest = interestItems.length
    ? Math.max(...interestItems.map((item) => parseFloat(item.interestRate)))
    : 0;

  const resetForm = () => {
    setDuration("");
    setInterest("");
    setValidFrom(new Date());   // reset to today
    setSelectedDuration(maxDropdownDuration || "");
    setSelectedInterest(maxDropdownInterest || "");
    setSelectedSlabId("");
    setDurationErrorValue("");
    setInterestErrorValue("");
  };

  const handleAdd = async (row) => {
    // always use manual inputs for saving

    if (isForkMode) {
      const payload = {
        termDepositMasterId: rowData?.id, // parent record id
        termDepositInterestSlabId: selectedSlabId,// ⬅️ CHANGE HERE (encoded id of selected duration)
        termDuration: duration,             // ⬅️ CHANGE HERE (number value)
        interestRate: interest,
        validFrom: format(validFrom, "yyyy-MM-dd"), // ⬅️ FIX (you wrote selectedDate before, but state is validFrom)
        active: true,
        editable: true,
      };

      try {
        setAdding(true);

        console.log("PreMature Payload:", payload);

        const newRecord = await dispatch(createTermDepositPreMatureInterest(payload)).unwrap(); // ✅ save to DB
        await dispatch(fetchTermDepositsInterestPreMatureByParentId(rowData?.id));   // ✅ refresh table

        // Close modal + reset
        setModalState({ open: false, mode: null });
        setPreMatureItems(prev => [...prev, newRecord]);
        resetForm();
      } catch (err) {
        console.error("Error while saving:", err);
      } finally {
        setAdding(false);
      }
    }
    else if (!duration || !interest) return;

    else if (!isForkMode) {
      const payload = {
        termDuration: Number(duration),   // manual duration
        interestRate: Number(interest),   // manual interest
        active: true,
        editable: true,
        termDepositMasterId: rowData?.id,
      };

      try {
        setAdding(true);
        const newRecord = await dispatch(createDepositInterest(payload)).unwrap();
        dispatch(fetchTermDepositsInterest(rowData?.id)); // refresh table

        setInterestItems(prev => [...prev, newRecord]);
        resetForm();
      } catch (err) {
        console.error("Failed to add interest:", err);
      } finally {
        setAdding(false);
      }
    }

  };

  const handleDelete = async (rowId, isFork) => {
    if (!rowId) return;

    try {
      if (isFork) {
        // 🔹 Find the record by id from state
        const record = preMatureItems.find((item) => item.id === rowId);
        if (!record) {
          return;
        }

        const payload = {
          id: record.id,
          termDuration: record.termDuration,
          interestRate: record.interestRate,
          validFrom: record.validFrom,
          active: false,
          editable: record.editable ?? true,
        };

        await dispatch(createTermDepositPreMatureInterest(payload)).unwrap();
        await dispatch(fetchTermDepositsInterestPreMatureByParentId(rowData?.id));

       setPreMatureItems(prev => prev.filter(item => item.id !== rowId));

        resetForm();
      } else {
        // 🔹 Normal Mode → Existing logic
        const payload = {
          id: rowId,
          active: false,
          termDepositMasterId: rowData?.id,
        };

        await dispatch(createDepositInterest(payload)).unwrap();
        await dispatch(fetchTermDepositsInterest(rowData?.id));

       setInterestItems(prev => prev.filter(item => item.id !== rowId));

        resetForm();
      }
    } catch (err) {
      console.error("Failed to delete record:", err);
    }
  };



  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isForkMode
          ? "Pre Mature Int."
          : rowData?.depositCode
            ? `${rowData.depositCode} - Interest Rates`
            : "Interest Rates"}
      </DialogTitle>

      <DialogContent>
        {/* ✅ New Grid Row for DepositName dropdown + Valid From */}

        {isForkMode && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
              <TextField
                label="Deposit Name"
                size="small"
                value={rowData?.depositName || ""}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                helperText="Deposit name"
                sx={{ mt: 2 }}
              />
            </Grid>


            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
              {/* <TextField
                label="Valid From"
                size="small"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                fullWidth
                placeholder="YYYY-MM-DD"
                required
                helperText="Enter valid from date"
                sx={{ mt: 2 }}
              /> */}

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Valid From"
                  value={validFrom}
                  onChange={(newValue) => {
                    if (newValue) {
                      setValidFrom(newValue); // keep as Date
                    }
                  }}
                  sx={{ mt: 2 }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      helperText: "Pick valid from date",

                    },

                  }}
                />
              </LocalizationProvider>

            </Grid>
          </Grid>
        )}

        {isForkMode && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Duration Dropdown */}
            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
              <FormControl size="small" fullWidth>
                <Select
                  size="small"
                  value={selectedDuration ?? ""}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setSelectedDuration(selected);

                    const match = interestItems.find(
                      (item) => item.termDuration === selected
                    );
                    if (match) {
                      setSelectedInterest(match.interestRate);
                      setSelectedSlabId(match.id);
                    } else {
                      setSelectedInterest("");
                      setSelectedSlabId("");
                    }
                  }}
                  fullWidth
                  displayEmpty
                  helperText="Select Duration to view Interest"
                >
                  <MenuItem value="">
                    <em>Select Duration</em>
                  </MenuItem>
                  {interestItems.map((item) => (
                    <MenuItem key={item.termDuration} value={item.termDuration}>
                      {item.termDuration} Months
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select Duration to view Interest</FormHelperText>
              </FormControl>
            </Grid>

            {/* Interest Box */}
            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
              <TextField
                label="Interest"
                size="small"
                value={selectedInterest ?? ""}
                onChange={() => { }}
                fullWidth
                inputProps={{ readOnly: true }}
                helperText="Rate of Interest as per selected duration"
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>

          <Grid item size={{ xs: 12, sm: 12, md: 5 }}>
            <TextField
              label="Duration"
              size="small"
              value={duration ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) { // ✅ only numbers allowed
                  setDuration(val);
                  const numVal = parseInt(val, 10);

                  if (isNaN(numVal)) {
                    setDurationErrorValue("Duration is required");
                  } else if (numVal < 0) {
                    setDurationErrorValue("Duration cannot be negative");
                  } else if (isForkMode && maxDropdownDuration !== null && numVal >= maxDropdownDuration) {
                    if (maxDropdownDuration != 0) {
                      setDurationErrorValue(`Duration must be less than ${maxDropdownDuration}`);
                    }
                  }
                  else if (
                    isForkMode &&
                    preMatureItems.some((item) => item.termDuration === numVal)
                  ) {
                    setDurationErrorValue("This duration (Months) already exists");
                  }
                  else {
                    setDurationErrorValue(""); // ✅ valid
                  }
                }
              }}
              fullWidth
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              helperText={
                durationErrorValue ||
                `Term Deposit Duration`
              }
              FormHelperTextProps={{
                sx: { color: durationErrorValue ? "red" : "grey" },
              }}
            />

          </Grid>

          <Grid item size={{ xs: 12, sm: 12, md: 5 }}>
            <TextField
              label="Interest"
              size="small"
              value={interest ?? ""}
              onChange={(e) => {
                const val = e.target.value;

                if (/^\d*\.?\d*$/.test(val)) { // ✅ allow numbers + decimal
                  setInterest(val);
                  const numVal = parseFloat(val);

                  if (isNaN(numVal)) {
                    setInterestErrorValue("Interest is required");
                  } else if (numVal < 0) {
                    setInterestErrorValue("Interest cannot be negative");
                  } else if (isForkMode && maxDropdownInterest !== null && numVal >= maxDropdownInterest) {
                    if (maxDropdownInterest != 0) {
                      setInterestErrorValue(`Interest must be less than ${maxDropdownInterest}`);
                    }
                  }
                  else {
                    setInterestErrorValue(""); // ✅ valid
                  }
                }
              }}
              fullWidth
              inputProps={{
                inputMode: "decimal",
              }}
              helperText={
                interestErrorValue ||
                `Interest Rate on Selected duration`
              }
              FormHelperTextProps={{
                sx: { color: interestErrorValue ? "red" : "grey" },
              }}
            />


          </Grid>
          <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
            <Button
              onClick={handleAdd}
              variant="contained"
              color="primary"
              fullWidth
              disabled={
                adding ||
                (!!interestErrorValue && isForkMode) ||
                (!!durationErrorValue && isForkMode)
              }
              startIcon={adding ? <CircularProgress size={16} /> : <Add />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Add New
            </Button>
          </Grid>
        </Grid>

        {isForkMode ? (
          preMatureLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Please wait loading data...</Typography>
            </Box>
          ) : preMatureError ? (
            <Typography color="error">{preMatureError}</Typography>
          ) : (
            <EnhancedTable
              data={(preMatureItems || []).filter(
                (item) => !selectedDuration || item.termDuration < selectedDuration
              )}
              columns={[
                {
                  id: "termDuration",
                  label: "Duration (Months)",
                  sortable: true,
                },
                {
                  id: "interestRate",
                  label: "Interest (%)",
                  sortable: true,
                },
                {
                  id: "validFrom",
                  label: "Date",
                  sortable: true,
                  render: (value) => new Date(value).toLocaleDateString(),
                },
                {
                  id: "delete",
                  label: "Delete",
                  sortable: false,
                  render: (_, row) => (
                    <IconButton color="error" onClick={() => handleDelete(row.id, true)}>
                      <Delete />
                    </IconButton>
                  ),
                },
              ]}
              title="Pre-Mature Interest Rates"
              loading={preMatureLoading}
              emptyMessage="No pre-mature interest records found"
            />
          )
        ) : (
          interestLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Please wait loading data...</Typography>
            </Box>
          ) : interestError ? (
            <Typography color="error">{interestError}</Typography>
          ) : interestItems.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Please wait loading data...</Typography>
            </Box>
          ) : (
            <EnhancedTable
              data={interestItems || []}
              columns={[
                {
                  id: "termDuration",
                  label: "Duration (Months)",
                  sortable: true,
                },
                {
                  id: "interestRate",
                  label: "Interest (%)",
                  sortable: true,
                },
                {
                  id: "delete",
                  label: "Delete",
                  sortable: false,
                  render: (_, row) => (
                    <IconButton color="error" onClick={() => handleDelete(row.id, isForkMode)}>
                      <Delete />
                    </IconButton>
                  ),
                },
              ]}
              title="Interest Rates"
              loading={interestLoading}
              emptyMessage="No interest records found"
            />
          )
        )}

      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          startIcon={<Cancel />}
          color="error"
          variant="outlined"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterestModal;
